/**
 * Recovery context hooks and selectors
 */

import { useContext, useMemo, useEffect, useState } from 'react';
import { RecoveryStateContext, RecoveryActionsContext } from './RecoveryProvider';
import { RecoveryStep } from './types';

/**
 * Hook to access recovery state
 * @returns {Object} Recovery state
 * @throws {Error} If used outside RecoveryProvider
 */
export function useRecovery() {
  const context = useContext(RecoveryStateContext);

  if (context === undefined || context === null) {
    throw new Error(
      'useRecovery must be used within a RecoveryProvider. ' +
      'Make sure your component is wrapped with <RecoveryProvider>.',
    );
  }

  return context;
}

/**
 * Hook to access recovery actions
 * @returns {Object} Recovery actions
 * @throws {Error} If used outside RecoveryProvider
 */
export function useRecoveryActions() {
  const context = useContext(RecoveryActionsContext);

  if (context === undefined || context === null) {
    throw new Error(
      'useRecoveryActions must be used within a RecoveryProvider. ' +
      'Make sure your component is wrapped with <RecoveryProvider>.',
    );
  }

  return context;
}

/**
 * Hook to check if recovery is required
 * @returns {boolean} True if recovery is needed and not done
 */
export function useIsRecoveryRequired() {
  const state = useRecovery();

  return useMemo(() => {
    return state.needsRecovery && state.step !== RecoveryStep.DONE;
  }, [state.needsRecovery, state.step]);
}

/**
 * Hook to get recovery progress
 * @returns {Object} Progress object with imported, total, and percent
 */
export function useRecoveryProgress() {
  const state = useRecovery();

  return useMemo(() => {
    return {
      imported: state.progress?.imported || 0,
      total: state.progress?.total || 0,
      percent: state.progress?.percent || 0,
    };
  }, [state.progress]);
}

/**
 * Hook to get recovery error
 * @returns {Object|null} Error object or null
 */
export function useRecoveryError() {
  const state = useRecovery();

  return useMemo(() => {
    return state.error || null;
  }, [state.error]);
}

/**
 * Hook to check if recovery is in progress
 * @returns {boolean} True if recovery is actively in progress
 */
export function useIsRecoveryInProgress() {
  const state = useRecovery();

  return useMemo(() => {
    const inProgressSteps = [
      RecoveryStep.VALIDATING,
      RecoveryStep.RESTORING,
      RecoveryStep.SSSS,
      RecoveryStep.VERIFYING,
    ];

    return inProgressSteps.includes(state.step);
  }, [state.step]);
}

/**
 * Hook to check if recovery modal should be shown
 * @returns {boolean} True if modal should be displayed
 */
export function useShowRecoveryModal() {
  const state = useRecovery();

  return useMemo(() => {
    return state.step === RecoveryStep.AWAIT_KEY || state.step === RecoveryStep.ERROR;
  }, [state.step]);
}

/**
 * Hook to get recovery duration
 * @returns {number|null} Duration in milliseconds or null
 */
export function useRecoveryDuration() {
  const state = useRecovery();
  const [now, setNow] = useState(() => Date.now());

  const isActive = state.startedAt && ![RecoveryStep.DONE, RecoveryStep.IDLE].includes(state.step);

  useEffect(() => {
    if (!isActive) return undefined;

    // Tick every second to refresh duration
    const intervalId = setInterval(() => setNow(Date.now()), 1000);
    // Also do an immediate tick to avoid waiting a full second
    setNow(Date.now());

    return () => clearInterval(intervalId);
  }, [isActive]);

  if (!isActive) return null;
  return Math.max(0, now - state.startedAt);
}

/**
 * Hook to check if recovery can be retried
 * @returns {boolean} True if retry is possible
 */
export function useCanRetryRecovery() {
  const state = useRecovery();

  return useMemo(() => {
    return state.step === RecoveryStep.ERROR && state.needsRecovery;
  }, [state.step, state.needsRecovery]);
}

/**
 * Hook to get human-readable step description
 * @returns {string} Step description
 */
export function useRecoveryStepDescription() {
  const state = useRecovery();

  return useMemo(() => {
    const descriptions = {
      [RecoveryStep.IDLE]: 'Ready',
      [RecoveryStep.AWAIT_KEY]: 'Waiting for recovery key',
      [RecoveryStep.VALIDATING]: 'Validating recovery key',
      [RecoveryStep.RESTORING]: 'Restoring encrypted data',
      [RecoveryStep.SSSS]: 'Setting up secure storage',
      [RecoveryStep.VERIFYING]: 'Verifying device',
      [RecoveryStep.DONE]: 'Recovery complete',
      [RecoveryStep.ERROR]: 'Recovery failed',
    };

    return descriptions[state.step] || 'Unknown';
  }, [state.step]);
}

/**
 * Combined hook for common recovery UI needs
 * @returns {Object} Combined recovery state and actions
 */
export function useRecoveryUI() {
  const state = useRecovery();
  const actions = useRecoveryActions();
  const isRequired = useIsRecoveryRequired();
  const isInProgress = useIsRecoveryInProgress();
  const showModal = useShowRecoveryModal();
  const canRetry = useCanRetryRecovery();
  const stepDescription = useRecoveryStepDescription();

  return useMemo(() => ({
    // State
    ...state,

    // Computed values
    isRequired,
    isInProgress,
    showModal,
    canRetry,
    stepDescription,

    // Actions
    ...actions,
  }), [
    state,
    isRequired,
    isInProgress,
    showModal,
    canRetry,
    stepDescription,
    actions,
  ]);
}
